import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { MapPin, AlertTriangle, Search, X, ChevronUp, ChevronDown } from 'lucide-react';
import { searchCustomers, getCustomerCoverageSummary } from '../services/customerApi';
import { useDebounce } from '@hooks/useDebounce';
import type { ClientCoverageSummary, LocationCoverageSummary } from '../types/clientCoverage';
import type { Customer } from '../types/clientClinic';
import './ClientCoveragePage.css';
import { withXmId } from '@shared/utils/xmId';

// Autocomplete Component
interface AutocompleteProps<T> {
  label: string;
  placeholder: string;
  value: T | null;
  onSelect: (item: T | null) => void;
  searchFn: (query: string) => Promise<T[]>;
  getDisplayValue: (item: T) => string;
  getKey: (item: T) => number;
  disabled?: boolean;
}

function Autocomplete<T>({
  label,
  placeholder,
  value,
  onSelect,
  searchFn,
  getDisplayValue,
  getKey,
  disabled = false,
}: AutocompleteProps<T>) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<T[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const debouncedQuery = useDebounce(query, 300);

  useEffect(() => {
    if (debouncedQuery.length < 2) {
      setResults([]);
      return;
    }

    const search = async () => {
      setIsLoading(true);
      try {
        const data = await searchFn(debouncedQuery);
        setResults(data);
      } catch (error) {
        console.error('Search error:', error);
        setResults([]);
      } finally {
        setIsLoading(false);
      }
    };

    search();
  }, [debouncedQuery, searchFn]);

  const handleSelect = (item: T) => {
    onSelect(item);
    setQuery('');
    setIsOpen(false);
    setResults([]);
  };

  const handleClear = () => {
    onSelect(null);
    setQuery('');
    setResults([]);
  };

  return (
    <div className="autocomplete-field">
      <label>{label}</label>
      {value ? (
        <div className="autocomplete-selected">
          <span>{getDisplayValue(value)}</span>
          {!disabled && (
            <button type="button" className="clear-btn" onClick={handleClear} title="Clear selection" aria-label="Clear selection">
              <X size={16} />
            </button>
          )}
        </div>
      ) : (
        <div className="autocomplete-input-wrapper">
          <Search size={16} className="search-icon" />
          <input
            type="text"
            placeholder={placeholder}
            aria-label={placeholder}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setIsOpen(true);
            }}
            onFocus={() => setIsOpen(true)}
            disabled={disabled}
          />
          {isOpen && (query.length >= 2 || results.length > 0) && (
            <div className="autocomplete-dropdown">
              {isLoading ? (
                <div className="autocomplete-loading">Searching...</div>
              ) : results.length > 0 ? (
                results.map((item) => (
                  <div
                    key={getKey(item)}
                    className="autocomplete-item"
                    onClick={() => handleSelect(item)}
                  >
                    {getDisplayValue(item)}
                  </div>
                ))
              ) : query.length >= 2 ? (
                <div className="autocomplete-empty">No results found</div>
              ) : null}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Sort types
type SortDirection = 'asc' | 'desc' | null;
type SortConfig = { key: string; direction: SortDirection };

// Sortable Column Header
interface SortableHeaderProps {
  label: string;
  sortKey: string;
  sortConfig: SortConfig;
  onSort: (key: string) => void;
  className?: string;
}

function SortableHeader({ label, sortKey, sortConfig, onSort, className }: Readonly<SortableHeaderProps>) {
  const isActive = sortConfig.key === sortKey;
  return (
    <th className={className}>
      <div className="coverage-th-content">
        <div className="coverage-th-sort-row" onClick={() => onSort(sortKey)}>
          <span className="coverage-th-label">{label}</span>
          <span className={`coverage-sort-chevrons ${isActive ? 'active' : ''}`}>
            <ChevronUp size={10} className={`coverage-sort-icon ${isActive && sortConfig.direction === 'asc' ? 'active' : ''}`} />
            <ChevronDown size={10} className={`coverage-sort-icon ${isActive && sortConfig.direction === 'desc' ? 'active' : ''}`} />
          </span>
        </div>
      </div>
    </th>
  );
}

// Numeric cell helper
function NumericCell({ value, className }: { value: number; className?: string }) {
  const cellClass = value === 0 ? 'coverage-cell-zero' : (className || '');
  return <td className={cellClass}>{value}</td>;
}

// Column definitions for the coverage grid
const numericColumns: { key: keyof LocationCoverageSummary; label: string; group: string }[] = [
  { key: 'clinicsInRadius', label: 'In Radius', group: 'counts' },
  { key: 'approvedForClientUse', label: 'Approved', group: 'counts' },
  { key: 'approvedForInjury', label: 'Injury', group: 'counts' },
  { key: 'erCount', label: 'ER', group: 'capabilities' },
  { key: 'afterHourCount', label: '24H', group: 'capabilities' },
  { key: 'patCount', label: 'PAT', group: 'capabilities' },
  { key: 'xRayCount', label: 'X-Ray', group: 'capabilities' },
  { key: 'dotCertifiedCount', label: 'DOT', group: 'capabilities' },
  { key: 'fitTestCount', label: 'FitTest', group: 'capabilities' },
  { key: 'occMedCount', label: 'OccMed', group: 'categories' },
  { key: 'orthoSurgeryCount', label: 'Ortho', group: 'categories' },
  { key: 'physicalTherapyCount', label: 'PT', group: 'categories' },
  { key: 'chiropracticCount', label: 'Chiro', group: 'categories' },
  { key: 'mentalHealthCount', label: 'MH', group: 'categories' },
  { key: 'painMgmtCount', label: 'Pain', group: 'categories' },
  { key: 'imagingCount', label: 'Img', group: 'categories' },
  { key: 'labCount', label: 'Lab', group: 'categories' },
  { key: 'audiometryCount', label: 'Audio', group: 'categories' },
  { key: 'erCategoryCount', label: 'ER(Cat)', group: 'categories' },
  { key: 'urgentCareCount', label: 'UC', group: 'categories' },
  { key: 'surgicalCenterCount', label: 'Surg', group: 'categories' },
  { key: 'dentistCount', label: 'Dent', group: 'categories' },
  { key: 'woundCareCount', label: 'Wound', group: 'categories' },
];

export default function ClientCoveragePage() {
  const navigate = useNavigate();
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [radiusMiles, setRadiusMiles] = useState(25);
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: '', direction: null });

  const { data: coverage, isLoading, error } = useQuery<ClientCoverageSummary>({
    queryKey: ['client-coverage', selectedCustomer?.id, radiusMiles],
    queryFn: () => getCustomerCoverageSummary(selectedCustomer!.id, radiusMiles),
    enabled: selectedCustomer !== null,
  });

  const handleSort = useCallback((key: string) => {
    setSortConfig((prev) => {
      if (prev.key !== key) return { key, direction: 'asc' };
      if (prev.direction === 'asc') return { key, direction: 'desc' };
      return { key: '', direction: null };
    });
  }, []);

  const searchCustomersFn = useCallback(async (query: string) => {
    return searchCustomers(query);
  }, []);

  // Build location display name
  const getLocationDisplay = (loc: LocationCoverageSummary): string => {
    const parts = [loc.locationName];
    if (loc.city) parts.push(loc.city);
    if (loc.state) parts.push(loc.state);
    return parts.join(', ');
  };

  // Sort locations
  const sortedLocations = useMemo(() => {
    if (!coverage) return [];
    const locations = [...coverage.locations];

    if (sortConfig.key && sortConfig.direction) {
      locations.sort((a, b) => {
        let cmp = 0;
        if (sortConfig.key === 'locationName') {
          const aVal = getLocationDisplay(a);
          const bVal = getLocationDisplay(b);
          cmp = aVal.localeCompare(bVal, undefined, { sensitivity: 'base' });
        } else {
          const aVal = (a[sortConfig.key as keyof LocationCoverageSummary] as number) ?? -1;
          const bVal = (b[sortConfig.key as keyof LocationCoverageSummary] as number) ?? -1;
          cmp = aVal - bVal;
        }
        return sortConfig.direction === 'asc' ? cmp : -cmp;
      });
    }

    return locations;
  }, [coverage, sortConfig]);

  return (
    <div className="coverage-page">
      <div className="coverage-header">
        <MapPin size={24} />
        <h1>Client Coverage</h1>
      </div>

      <div className="coverage-controls">
        <Autocomplete<Customer>
          label="Client"
          placeholder="Search customers..."
          value={selectedCustomer}
          onSelect={setSelectedCustomer}
          searchFn={searchCustomersFn}
          getDisplayValue={(c) => c.name}
          getKey={(c) => c.id}
        />

        <div className="coverage-radius-group">
          <label htmlFor="coverage-radius-select">Radius</label>
          <select
            id="coverage-radius-select"
            className="coverage-radius-select"
            value={radiusMiles}
            onChange={(e) => setRadiusMiles(Number(e.target.value))}
            aria-label="Radius"
          >
            <option value={5}>5 mi</option>
            <option value={10}>10 mi</option>
            <option value={15}>15 mi</option>
            <option value={25}>25 mi</option>
            <option value={50}>50 mi</option>
            <option value={100}>100 mi</option>
          </select>
        </div>
      </div>

      {/* Warning when some locations are not geocoded */}
      {coverage && coverage.geocodedLocations < coverage.totalLocations && (
        <div className="coverage-warning">
          <AlertTriangle size={16} />
          <span>
            {coverage.totalLocations - coverage.geocodedLocations} of {coverage.totalLocations} locations
            are not geocoded and cannot be searched.
          </span>
        </div>
      )}

      {/* Loading state */}
      {isLoading && (
        <div className="coverage-loading">
          <div className="loading-spinner" />
          <p>Loading coverage data...</p>
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="coverage-error">
          Failed to load coverage data. Please try again.
        </div>
      )}

      {/* Empty state — no customer selected */}
      {!selectedCustomer && !isLoading && (
        <div className="coverage-empty">
          <MapPin size={48} strokeWidth={1.5} />
          <h3>Select a client to view coverage</h3>
          <p>Search for a customer above to see clinic coverage near their locations.</p>
        </div>
      )}

      {/* Summary bar */}
      {coverage && !isLoading && (
        <div className="coverage-summary-bar">
          <span className="coverage-summary-customer">{withXmId(coverage.customerName, selectedCustomer?.xmId)}</span>
          <span className="coverage-summary-stat">
            {coverage.totalLocations} location{coverage.totalLocations !== 1 ? 's' : ''}
          </span>
          <span className="coverage-summary-stat">
            {coverage.geocodedLocations} geocoded
          </span>
          <span className="coverage-summary-stat">
            {radiusMiles} mi radius
          </span>
        </div>
      )}

      {/* Coverage grid table */}
      {coverage && !isLoading && (
        <div className="coverage-table-container">
          <table className="coverage-table">
            <colgroup>
              <col className="col-coverage-location" />
              {numericColumns.map((col) => (
                <col key={col.key} className="col-coverage-numeric" />
              ))}
            </colgroup>
            <thead>
              <tr className="coverage-group-header">
                <th></th>
                <th colSpan={3}>Number of Clinics</th>
                <th colSpan={6}>Capability Flags</th>
                <th colSpan={14}>Categories</th>
              </tr>
              <tr>
                <SortableHeader
                  label="Location"
                  sortKey="locationName"
                  sortConfig={sortConfig}
                  onSort={handleSort}
                  className="coverage-th-location"
                />
                {numericColumns.map((col) => (
                  <SortableHeader
                    key={col.key}
                    label={col.label}
                    sortKey={col.key}
                    sortConfig={sortConfig}
                    onSort={handleSort}
                    className="coverage-th-numeric"
                  />
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedLocations.length === 0 ? (
                <tr>
                  <td colSpan={24} className="coverage-empty-message">
                    No locations found for this customer.
                  </td>
                </tr>
              ) : (
                sortedLocations.map((loc) => (
                  <tr
                    key={loc.customerLocationId}
                    className="coverage-row-clickable"
                    onClick={() => navigate(
                      `/client-coverage/${loc.customerLocationId}?customerId=${selectedCustomer!.id}&radiusMiles=${radiusMiles}`
                    )}
                  >
                    <td className="coverage-cell-location" title={getLocationDisplay(loc)}>
                      {getLocationDisplay(loc)}
                    </td>
                    {loc.latitude == null ? (
                      numericColumns.map((col) => (
                        <td key={col.key} className="coverage-cell-na">&ndash;</td>
                      ))
                    ) : (
                      numericColumns.map((col) => (
                        <NumericCell
                          key={col.key}
                          value={loc[col.key] as number}
                        />
                      ))
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
